/* PBSS v15 cart gift validator (storefront cleanup only; not checkout security).
   Validates ONLY gift lines created by PBSS with _pbss_bundle_id and
   _pbss_item_type='gift'. Requires the PBSS block to be on the page.
   Does not modify gifts managed by unrelated apps. */
(() => {
  'use strict';
  if (window.__pbssV15GiftGuard) return;
  window.__pbssV15GiftGuard = true;
  const base = () => window.Shopify?.routes?.root || '/';
  const configurations = () => [...document.querySelectorAll('[data-pbss-root]')].map(root => {
    try {
      const config = JSON.parse(root.querySelector('[data-pbss-data]').textContent);
      const tiers = [...root.querySelectorAll('[data-pbss-tier]')].map(el => ({
        index: Number(el.dataset.tierIndex), quantity: Number(el.dataset.quantity)
      }));
      return { config, tiers };
    } catch (e) { console.warn('PBSS validator: bad block config', e); return null; }
  }).filter(Boolean);
  let running = false, retry = false, timer;
  async function check() {
    if (running) { retry = true; return; }
    const configs = configurations();
    if (!configs.length) return;
    running = true;
    try {
      const response = await fetch(`${base()}cart.js`, { credentials:'same-origin',cache:'no-store' });
      if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
      const cart = await response.json();
      const byGroup = new Map();
      for (const line of cart.items) {
        const id = line.properties?._pbss_bundle_id;
        if (!id || !line.properties?._pbss_item_type) continue;
        if (!byGroup.has(id)) byGroup.set(id, []);
        byGroup.get(id).push(line);
      }
      const changes = [];
      for (const [bundleId, lines] of byGroup) {
        const designs = lines.filter(l => l.properties?._pbss_item_type === 'design');
        const gifts = lines.filter(l => l.properties?._pbss_item_type === 'gift');
        if (!gifts.length) continue;
        const tier = Number(gifts[0].properties?._pbss_tier || designs[0]?.properties?._pbss_tier);
        const mode = String(gifts[0].properties?._pbss_mode || designs[0]?.properties?._pbss_mode);
        const required = configs.find(c => c.tiers.some(t => t.index === tier));
        // No config on page? Leave lines unchanged rather than removing by guess.
        if (!required) continue;
        const target = required.tiers.find(t => t.index === tier);
        const validIds = new Set(required.config.products.flatMap(p => p.variants.map(v => String(v.id))));
        const paidCount = designs.filter(line =>
          validIds.has(String(line.variant_id)) &&
          (mode === 'subscription' ? Boolean(line.selling_plan_allocation) : !line.selling_plan_allocation)
        ).reduce((sum, line) => sum + line.quantity, 0);
        const qualifies = target.quantity > 0 && paidCount >= target.quantity &&
          (mode === 'subscription' || (mode === 'one_time' && Boolean(required.config.settings.oneTimeGifts)));
        const used = new Map();
        for (const line of gifts) {
          const index = Number(line.properties?._pbss_gift_index);
          const configGift = required.config.gifts.find(g => g.index === index);
          const matches = configGift && configGift.enabled && configGift.type === 'product' &&
            String(configGift.variantId) === String(line.variant_id) && tier >= Number(configGift.tier);
          const allowed = qualifies && matches ? (index === 1 || index === 2 ? tier : 1) : 0;
          const key = String(index);
          const remaining = Math.max(0, allowed - (used.get(key) || 0));
          const keep = Math.min(line.quantity, remaining);
          used.set(key, (used.get(key) || 0) + keep);
          if (keep !== line.quantity) changes.push({key: line.key, quantity: keep});
        }
      }
      for (const change of changes) {
        const response = await fetch(`${base()}cart/change.js`, {
          method:'POST',credentials:'same-origin',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({id:change.key,quantity:change.quantity})
        });
        if (!response.ok) throw new Error(`Gift correction failed (${response.status})`);
      }
      if (changes.length) {
        document.dispatchEvent(new CustomEvent('pbss:gifts-validated',{detail:{changes}}));
        document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));
      }
    } catch (err) { console.warn('PBSS validator:',err); }
    finally { running = false; if (retry) { retry=false;schedule(); } }
  }
  function schedule() { clearTimeout(timer); timer=setTimeout(check,600); }
  document.addEventListener('pbss:cart-added',schedule);
  document.addEventListener('cart:refresh',schedule);
  document.addEventListener('shopify:section:load',schedule);
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',schedule,{once:true});
  else schedule();
  window.pbssValidateGifts = check;
})();
